/* eslint-disable jsdoc/require-jsdoc */
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useMemo } from 'react';
import getPurchaseStepName from 'utils/purchase/getPurchaseStepName';
import { formatPercent } from 'utils/Helpers';
import { digitalDiscountsTypes } from 'models/parseTrip';
import { allocateSeatsByCategory } from 'utils/privilegedSeatAllocation';
import useShowPricesByGroup from 'hooks/useShowPricesByGroup';
import useMixDiscountFaresByAvailability from 'hooks/useMixDiscountFaresByAvailability';

/**
 * Build an empty per-category breakdown entry.
 * @returns {object} Zero-initialized breakdown entry.
 */
const buildBreakdownEntry = () => ({
  count: 0,
  total: 0,
  totalSpecialDiscount: 0,
  countWithSpecialDiscount: 0,
  countWithoutSpecialDiscount: 0,
  normalTotal: 0,
  discount: 0,
  discountPercent: null,
  normalDiscountPercent: null,
});

/**
 * Converts passenger selection to a simplified format based on GrowthBook feature.
 * @param {Object} passengerSelection - The original passenger selection object.
 * @param {string} typeCategory - The category type ('simple' or 'categories'). When 'simple', collapses to { general: total }.
 * @returns {Object} The simplified passenger selection object.
 */
const convertToSimple = (passengerSelection, typeCategory) => {
  if (typeCategory === 'simple') {
    const total = Object.values(passengerSelection).reduce((acc, value) => acc + value, 0);
    return { general: total };
  }
  return passengerSelection;
};

/**
 * Optimized pricing calculation for overbooking scenarios
 * @param {object} options - The options for calculating overbooking pricing
 * @param {number} options.passengersCount - Total number of passengers
 * @param {Array} options.passengerTypes - Array of passenger types with pricing
 * @param {object} options.pricing - Original pricing object for base structure
 * @returns {object} Complete pricing breakdown for overbooking
 */
const calculateOverbookingPricing = ({ passengersCount, passengerTypes, pricing }) => {
  const oversoldType = passengerTypes.find((type) => type.type === 'oversold');

  if (!oversoldType) {
    // Fallback structure if no oversold type found
    return {
      amount: 0,
      total: 0,
      totalPercentDiscount: '0%',
      totalBeforeDiscount: 0,
      discount: 0,
      discountType: null,
      passengersCount: 0,
      luggage: 0,
      taxes: 0,
      networks: 0,
      insurance: 0,
      passengerBreakdown: { general: { count: 0, total: 0 } },
      hasSingleTripDiscount: false,
      singleTripDiscount: null,
    };
  }

  const oversoldPrice = Number(oversoldType.total) || 0;
  const total = oversoldPrice * passengersCount;

  // Create breakdown for all passenger types (set to 0 except oversold)
  const passengerBreakdown = {};

  // Initialize all passenger types to 0
  passengerTypes.forEach((type) => {
    passengerBreakdown[type.type] = {
      count: 0,
      total: 0,
      discount: 0,
      discountPercent:
        type.type !== 'general'
          ? formatPercent(pricing.totalBeforeDiscount || 0, Number(type.total) || 0)
          : null,
    };
  });

  // Set general breakdown
  passengerBreakdown.general = {
    count: 0,
    total: 0,
    totalSpecialDiscount: 0,
    countWithSpecialDiscount: 0,
    countWithoutSpecialDiscount: 0,
    normalTotal: 0,
    discount: 0,
    discountPercent: null,
    normalDiscountPercent: null,
  };

  // Set oversold breakdown
  passengerBreakdown.oversold = {
    count: passengersCount,
    total,
    totalSpecialDiscount: total,
    countWithSpecialDiscount: passengersCount,
    countWithoutSpecialDiscount: 0,
    normalTotal: 0,
    discount: 0,
    discountPercent: null,
    normalDiscountPercent: null,
  };

  return {
    amount: total,
    total,
    totalPercentDiscount: '0%',
    totalBeforeDiscount: total,
    discount: 0,
    discountType: pricing.discountType || null,
    passengersCount,
    luggage: 0,
    taxes: 0,
    networks: 0,
    insurance: 0,
    passengerBreakdown,
    hasSingleTripDiscount: false,
    singleTripDiscount: null,
  };
};

/**
 * Gets the remaining availability for the active discount in a pricing object.
 * @param {object} pricing - Trip pricing object.
 * @returns {number|null} Active discount availability, or null when uncapped.
 */
export const getDiscountAvailability = (pricing = {}) => {
  if (pricing?.discountAvailability != null) {
    const topLevelAvailability = Number(pricing.discountAvailability);
    if (Number.isFinite(topLevelAvailability)) return Math.max(topLevelAvailability, 0);
  }

  const currentDiscountName = pricing?.discountType || pricing?.name;
  const currentDiscount = (pricing?.providerDiscounts || []).find(
    (discount) => discount.name === currentDiscountName,
  );
  const providerAvailability = Number(currentDiscount?.availability);

  return Number.isFinite(providerAvailability) ? Math.max(providerAvailability, 0) : null;
};

/**
 * Gets a discount availability from a provider discount object.
 * @param {object} discount - Provider discount object.
 * @returns {number|null} Discount availability, or null when uncapped.
 */
const getProviderDiscountAvailability = (discount = {}) => {
  if (discount?.availability == null) return null;

  const availability = Number(discount.availability);
  return Number.isFinite(availability) ? Math.max(availability, 0) : null;
};

/**
 * Gets digital provider discounts that can be used after the active discount.
 * @param {object} pricing - Trip pricing object.
 * @returns {Array} Provider discounts available as fallback tiers.
 */
const getFallbackDiscountCandidates = (pricing = {}) => {
  const currentDiscountName = pricing?.discountType || pricing?.name;

  return (pricing?.providerDiscounts || []).filter((discount) => {
    if (discount.name === currentDiscountName) return false;
    if (!digitalDiscountsTypes.includes(discount.name)) return false;
    if (discount.total == null) return false;

    return true;
  });
};

/**
 * Gets the next digital discount that can cover the whole selected quantity.
 * @param {object} options - Helper options.
 * @param {object} options.pricing - Trip pricing object.
 * @param {number} options.selectedCount - Total selected seats for the trip.
 * @returns {object|undefined} Fallback provider discount.
 */
export const getFallbackDiscountForSelection = ({ pricing = {}, selectedCount = 0 }) => {
  const normalizedSelectedCount = Number(selectedCount);

  return getFallbackDiscountCandidates(pricing).find((discount) => {
    const availability = getProviderDiscountAvailability(discount);
    return availability === null || availability >= normalizedSelectedCount;
  });
};

/**
 * Resolves how general passengers should be assigned across discounted, fallback, and regular fares.
 * @param {object} options - Helper options.
 * @param {object} options.pricing - Trip pricing object.
 * @param {number} options.selectedCount - Total general passengers selected.
 * @param {boolean} options.mixDiscountFaresByAvailability - Whether mixed fares are enabled.
 * @param {number} options.activeUnitAmount - Active discount amount without taxes.
 * @param {number} options.regularUnitAmount - Regular amount without taxes.
 * @returns {object} Fare assignment details.
 */
export const resolveFareAllocation = ({
  pricing = {},
  selectedCount = 0,
  mixDiscountFaresByAvailability = false,
  activeUnitAmount,
  regularUnitAmount,
}) => {
  const normalizedSelectedCount = Math.max(Number(selectedCount) || 0, 0);
  const discountedUnitTotal = Number(pricing.total || 0);
  const regularUnitTotal = Number(pricing.totalBeforeDiscount ?? pricing.total ?? 0);
  const discountedUnitAmount = Number(activeUnitAmount ?? pricing.amount ?? discountedUnitTotal);
  const resolvedRegularUnitAmount = Number(regularUnitAmount ?? regularUnitTotal);
  const discountAvailability = getDiscountAvailability(pricing);
  const hasValidDiscount = Boolean(pricing.providerDiscount && discountAvailability !== null);
  const discountAvailabilityExceeded =
    hasValidDiscount && normalizedSelectedCount > discountAvailability;
  const shouldMixDiscountFares =
    mixDiscountFaresByAvailability && discountAvailabilityExceeded && discountAvailability > 0;
  const fallbackDiscount =
    discountAvailabilityExceeded && !shouldMixDiscountFares
      ? getFallbackDiscountForSelection({ pricing, selectedCount: normalizedSelectedCount })
      : null;
  const resolvedDiscountUnitTotal = Number(fallbackDiscount?.total ?? discountedUnitTotal);
  const resolvedDiscountUnitAmount = Number(
    fallbackDiscount?.amount ?? activeUnitAmount ?? pricing.amount ?? resolvedDiscountUnitTotal,
  );

  const fareSegments = [];

  const addFareSegment = ({ type, name, count, unitTotal, unitAmount }) => {
    const normalizedCount = Math.max(Number(count) || 0, 0);
    if (!normalizedCount) return;

    fareSegments.push({
      type,
      name,
      count: normalizedCount,
      unitTotal,
      unitAmount,
      total: unitTotal * normalizedCount,
      amount: unitAmount * normalizedCount,
      discount: Math.max(regularUnitTotal - unitTotal, 0) * normalizedCount,
    });
  };

  if (shouldMixDiscountFares) {
    let remainingCount = normalizedSelectedCount;
    const activeDiscountedCount = Math.min(remainingCount, discountAvailability);

    addFareSegment({
      type: 'active_discount',
      name: pricing.discountType || pricing.name,
      count: activeDiscountedCount,
      unitTotal: discountedUnitTotal,
      unitAmount: discountedUnitAmount,
    });
    remainingCount -= activeDiscountedCount;

    getFallbackDiscountCandidates(pricing).some((discount) => {
      if (!remainingCount) return true;

      const availability = getProviderDiscountAvailability(discount);
      const fallbackCount =
        availability === null ? remainingCount : Math.min(remainingCount, availability);

      addFareSegment({
        type: 'fallback_discount',
        name: discount.name,
        count: fallbackCount,
        unitTotal: Number(discount.total),
        unitAmount: Number(discount.amount ?? discount.total),
      });
      remainingCount -= fallbackCount;

      return remainingCount === 0;
    });

    addFareSegment({
      type: 'regular',
      count: remainingCount,
      unitTotal: regularUnitTotal,
      unitAmount: resolvedRegularUnitAmount,
    });
  } else if (hasValidDiscount && (!discountAvailabilityExceeded || fallbackDiscount)) {
    addFareSegment({
      type: fallbackDiscount ? 'fallback_discount' : 'active_discount',
      name: fallbackDiscount?.name ?? pricing.discountType ?? pricing.name,
      count: normalizedSelectedCount,
      unitTotal: resolvedDiscountUnitTotal,
      unitAmount: resolvedDiscountUnitAmount,
    });
  } else {
    addFareSegment({
      type: 'regular',
      count: normalizedSelectedCount,
      unitTotal: regularUnitTotal,
      unitAmount: resolvedRegularUnitAmount,
    });
  }

  const discountedSegments = fareSegments.filter((segment) => segment.type !== 'regular');
  const regularSegments = fareSegments.filter((segment) => segment.type === 'regular');
  const discountedCount = discountedSegments.reduce((sum, segment) => sum + segment.count, 0);
  const activeDiscountedCount = fareSegments
    .filter((segment) => segment.type === 'active_discount')
    .reduce((sum, segment) => sum + segment.count, 0);
  const activeDiscountedTotal = fareSegments
    .filter((segment) => segment.type === 'active_discount')
    .reduce((sum, segment) => sum + segment.total, 0);
  const fallbackDiscountedCount = fareSegments
    .filter((segment) => segment.type === 'fallback_discount')
    .reduce((sum, segment) => sum + segment.count, 0);
  const regularCount = regularSegments.reduce((sum, segment) => sum + segment.count, 0);
  const totalWithDiscount = discountedSegments.reduce((sum, segment) => sum + segment.total, 0);
  const totalWithoutDiscount = regularSegments.reduce((sum, segment) => sum + segment.total, 0);
  const total = totalWithDiscount + totalWithoutDiscount;
  const amount = fareSegments.reduce((sum, segment) => sum + segment.amount, 0);
  const totalBeforeDiscount = regularUnitTotal * normalizedSelectedCount;
  const discount = fareSegments.reduce((sum, segment) => sum + segment.discount, 0);
  const unitDiscountAmount = Math.max(regularUnitTotal - resolvedDiscountUnitTotal, 0);
  const blendedDiscountPercent =
    totalBeforeDiscount && total ? formatPercent(totalBeforeDiscount, total) : 0;
  const perSeatTotals = fareSegments.flatMap((segment) => {
    return Array.from({ length: segment.count }, () => segment.unitTotal);
  });

  return {
    discountAvailability,
    discountAvailabilityExceeded,
    shouldMixDiscountFares,
    fallbackDiscount,
    fareSegments,
    discountedCount,
    activeDiscountedCount,
    activeDiscountedTotal,
    fallbackDiscountedCount,
    regularCount,
    discountedUnitTotal: resolvedDiscountUnitTotal,
    regularUnitTotal,
    totalWithDiscount,
    totalWithoutDiscount,
    total,
    amount,
    discount,
    unitDiscountAmount,
    blendedDiscountPercent,
    perSeatTotals,
    hasDiscountedFare: discount > 0,
  };
};

/**
 * Calculate the pricing for a trip.
 * @param {object} options - The options for calculating the pricing.
 * @param {object} options.pricing - The pricing information for the trip.
 * @param {number} options.passengersCount - The number of passengers.
 * @param {string} options.typeCategory - The category type for pricing calculation.
 * @param {object} options.passengerSelection - Contains the selected passengers in search.
 * @param {Array} options.passengerTypes - Contains the pricing for each passenger type.
 * @param {boolean} options.overbooking - If true, additional passengers will be categorized as 'oversold'.
 * @param {boolean} options.mixDiscountFaresByAvailability - Whether to mix discount and regular fares when availability is exceeded.
 * @returns {object} The calculated trip pricing.
 */

const HALF_PRICE_FALLBACK_PAIRS = {
  older: 'older_half',
  disadvantaged_youth: 'disadvantaged_youth_half',
};

/**
 * Moves overflowed passengers of a gratuity type into its half-price variant
 * when slots remain. Mutates the accumulator counts in place.
 *
 * @param {object} acc - Accumulator with passenger counts per type.
 * @param {string} key - Passenger type whose selection exceeded availability.
 * @param {object} passengerTypesMap - Passenger types indexed by type key.
 * @param {number} overflow - Passengers exceeding the original type availability.
 * @returns {void}
 */
const distributeHalfPriceOverflow = (acc, key, passengerTypesMap, overflow) => {
  const halfKey = HALF_PRICE_FALLBACK_PAIRS[key];
  if (!halfKey) return;

  const halfAvailability = passengerTypesMap[halfKey]?.availability ?? 0;
  const currentHalfCount = acc[halfKey] || 0;
  const remainingHalfAvailability = Math.max(halfAvailability - currentHalfCount, 0);
  if (remainingHalfAvailability <= 0) return;

  // Move as many overflowed passengers as possible into the half-price variant
  const halfSlots = Math.min(overflow, remainingHalfAvailability);

  acc[halfKey] = currentHalfCount + halfSlots;
  acc.general -= halfSlots;
};

export const getTripPricing = ({
  pricing = {},
  passengersCount,
  passengerSelection = {},
  passengerTypes = [],
  typeCategory = 'categories',
  overbooking = false,
  halfPriceFallbackEnabled = false,
  mixDiscountFaresByAvailability = false,
}) => {
  // Handle overbooking scenario first - early return for efficiency
  if (overbooking && passengersCount > 0) {
    return calculateOverbookingPricing({ passengersCount, passengerTypes, pricing });
  }

  if (passengerSelection.adult) {
    passengerSelection.general = passengerSelection.adult;
    delete passengerSelection.adult;
  }
  passengerSelection = convertToSimple(passengerSelection, typeCategory);

  // Index passenger types by type key for O(1) lookup during half-price overflow distribution
  const passengerTypesMap = halfPriceFallbackEnabled
    ? passengerTypes.reduce((acc, pt) => ({ ...acc, [pt.type]: pt }), {})
    : {};

  const passengerAvailabilityOrder = passengerTypes.reduce(
    (acc, item) => {
      const key = item.type;

      // Check if the key exists in the acc object (accumulator) and the availability is lower
      if (acc[key] !== undefined && item.availability < acc[key]) {
        const overflow = acc[key] - item.availability;
        acc.general += overflow;
        acc[key] = item.availability;

        // Redirect overflow to half-price variant (e.g. older → older_half) before falling back to general
        if (halfPriceFallbackEnabled)
          distributeHalfPriceOverflow(acc, key, passengerTypesMap, overflow);
      }

      return acc;
    },
    { ...passengerSelection },
  );

  const generalCount = passengerAvailabilityOrder?.general || 0;
  const totalSelectionCount = Object.values(passengerAvailabilityOrder).reduce(
    (acc, val) => acc + val,
    0,
  );
  // Validate if the pricing has the single_trip_discount structure
  const hasSingleTripDiscount =
    pricing.providerDiscounts &&
    pricing.providerDiscounts.some(
      (discount) =>
        discount.name === 'single_trip_discount' &&
        'amount' in discount &&
        'taxes' in discount &&
        'total' in discount,
    );

  // Gets the normal price with digital discount or use the normal price
  const digitalDiscount =
    pricing.providerDiscounts &&
    pricing.providerDiscounts.find(
      (discount) =>
        digitalDiscountsTypes.includes(discount.name) || discount.name === 'single_trip_discount',
    );

  // General fare used when no discount can cover the full selection.
  const generalFareTotal = Number(pricing.totalBeforeDiscount ?? pricing.total ?? 0);

  const totalPerGeneral = pricing.total || 0;
  const amountPerPassenger = pricing.amount || 0;
  const discountPerPassenger = pricing.providerDiscount?.amount || 0;
  const breakdown = pricing.breakdown || {};
  const luggagePerPassenger = breakdown.equipaje || 0;
  const networksPerPassenger = breakdown.redes || 0;
  const insurancePerPassenger = breakdown.seguros || 0;
  const taxesFromBreakdown = breakdown.impuestos || 0;
  const taxesFromPricing = pricing.taxes || 0;
  const taxesPerPassenger = taxesFromPricing > 0 ? taxesFromPricing : taxesFromBreakdown;
  const normalAmountPerPassenger = Math.max(generalFareTotal - taxesPerPassenger, 0);

  // Calculate costs for general passengers
  const generalPassengerCount = Math.min(passengersCount, generalCount);
  const additionalPassengerCount = Math.max(passengersCount - totalSelectionCount, 0);
  const totalGeneralCount =
    passengerTypes.length <= 1 ? passengersCount : generalPassengerCount + additionalPassengerCount;

  let total =
    passengerTypes.length <= 1
      ? passengersCount * totalPerGeneral
      : totalPerGeneral * totalGeneralCount;
  let amount = amountPerPassenger * totalGeneralCount;
  let discount = discountPerPassenger * totalGeneralCount;

  // Calculate costs for other passenger types

  const fareAllocation = resolveFareAllocation({
    pricing,
    selectedCount: totalGeneralCount,
    mixDiscountFaresByAvailability,
    activeUnitAmount: amountPerPassenger,
    regularUnitAmount: normalAmountPerPassenger,
  });
  const {
    discountAvailabilityExceeded,
    shouldMixDiscountFares,
    fallbackDiscount,
    fareSegments,
    activeDiscountedCount,
    activeDiscountedTotal,
    discountedUnitTotal,
    discountedCount: generalWitSpecialDiscountCount,
    totalWithDiscount: generalTotalWithDiscount,
    totalWithoutDiscount: generalTotalWithoutDiscount,
    blendedDiscountPercent,
  } = fareAllocation;
  const selectedDiscountTotal = discountedUnitTotal;

  if (shouldMixDiscountFares) {
    total = fareAllocation.total;
    amount = fareAllocation.amount;
    discount = fareAllocation.discount;
  } else if (discountAvailabilityExceeded) {
    total = generalTotalWithDiscount + generalTotalWithoutDiscount;
    amount = fareAllocation.amount;
    discount = fallbackDiscount ? fareAllocation.discount : 0;
  } else if (passengerTypes.length >= 2) {
    total = generalTotalWithDiscount + generalTotalWithoutDiscount;
  }

  const generalSpecialDiscountCount = shouldMixDiscountFares
    ? activeDiscountedCount
    : generalWitSpecialDiscountCount;
  const generalSpecialDiscountTotal = shouldMixDiscountFares
    ? activeDiscountedTotal
    : generalTotalWithDiscount;
  const generalRegularCount = totalGeneralCount - generalSpecialDiscountCount;
  const generalRegularTotal = total - generalSpecialDiscountTotal;
  const pricingSegments =
    shouldMixDiscountFares && fareSegments.length > 1
      ? fareSegments.map((segment) => ({
          id: `${segment.type}-${segment.name || 'regular'}-${segment.unitTotal}`,
          count: segment.count,
          total: segment.total,
          discountPercent: segment.discount
            ? formatPercent(generalFareTotal, segment.unitTotal)
            : null,
          hasDiscountIcon: segment.type === 'active_discount',
        }))
      : null;

  let generalDiscountPercent = formatPercent(pricing.totalBeforeDiscount, selectedDiscountTotal);
  if (
    !pricing.providerDiscount ||
    (discountAvailabilityExceeded && !fallbackDiscount && !shouldMixDiscountFares)
  ) {
    generalDiscountPercent = null;
  } else if (shouldMixDiscountFares) {
    generalDiscountPercent = blendedDiscountPercent;
  }

  const passengerBreakdown = {
    general: {
      count: totalGeneralCount,
      total,
      totalSpecialDiscount: generalSpecialDiscountTotal,
      countWithSpecialDiscount: generalSpecialDiscountCount,
      countWithoutSpecialDiscount: generalRegularCount,
      normalTotal: generalRegularTotal,
      discount,
      discountPercent: generalDiscountPercent,
      pricingSegments,
      normalDiscountPercent:
        !discountAvailabilityExceeded &&
        passengerTypes.length >= 2 &&
        generalTotalWithoutDiscount &&
        digitalDiscount
          ? formatPercent(digitalDiscount.amount + digitalDiscount.taxes, generalFareTotal)
          : null,
    },
  };

  let remainingPassengersCount = passengersCount - totalGeneralCount;

  passengerTypes.forEach((passengerType) => {
    const { type, total: categoryPrice } = passengerType;
    if (type === 'general') {
      return;
    }
    const totalPerType = parseFloat(categoryPrice) || 0;
    const typeCount = Math.min(remainingPassengersCount, passengerAvailabilityOrder[type] || 0);
    total += totalPerType * typeCount;
    amount += totalPerType * typeCount;

    passengerBreakdown[type] = {
      count: typeCount,
      total: totalPerType * typeCount,
      discount: 0,
      discountPercent: formatPercent(pricing.totalBeforeDiscount, totalPerType),
    };
    remainingPassengersCount -= typeCount;
  });

  let totalPercentDiscount = 0;
  if (shouldMixDiscountFares && pricing.totalBeforeDiscount) {
    totalPercentDiscount = blendedDiscountPercent;
  } else if ((!discountAvailabilityExceeded || fallbackDiscount) && pricing.totalBeforeDiscount) {
    totalPercentDiscount = formatPercent(pricing.totalBeforeDiscount, selectedDiscountTotal);
  }

  return {
    amount,
    total,
    totalPercentDiscount,
    totalBeforeDiscount: (pricing.totalBeforeDiscount || 0) * passengersCount,
    discount,
    discountType: pricing.discountType,
    passengersCount,
    luggage: luggagePerPassenger * passengersCount,
    taxes: taxesPerPassenger * passengersCount,
    networks: networksPerPassenger * passengersCount,
    insurance: insurancePerPassenger * passengersCount,
    passengerBreakdown,
    fareAllocation,
    hasSingleTripDiscount,
    singleTripDiscount: hasSingleTripDiscount ? digitalDiscount : null,
  };
};

/**
 * Calculate trip pricing based on individual seat prices.
 *
 * When `passengerSelection` is provided (and non-empty), seats are mapped to
 * passenger categories positionally, iterating in `ORDERED_PASSENGER_CATEGORIES`
 * order. Trailing seats that exceed the selection size fall back to `general`.
 * When `passengerSelection` is empty or missing, the function returns a single
 * `general` breakdown (legacy behaviour for callers that don't have the search).
 *
 * @param {object} options
 * @param {Array} [options.selectedSeats=[]] - Selected seats with prices.
 * @param {object} [options.pricing={}] - Original pricing object (tax/discount info).
 * @param {object} [options.passengerSelection] - Search selection (e.g. { general: 1, minor: 1, older: 1 }).
 * @returns {object} Trip pricing with `passengerBreakdown` keyed by canonical type.
 */
export const getTripPricingBySeatPrice = ({
  selectedSeats = [],
  pricing = {},
  passengerSelection,
} = {}) => {
  const regularSeats = selectedSeats.filter((seat) => !seat.isPickedAsAdjacent);
  const defaultSeatPrice = Number(pricing.total ?? pricing.amount ?? 0);
  const seatPrice = (seat) => Number(seat.price ?? defaultSeatPrice);

  const total = regularSeats.reduce((sum, seat) => sum + seatPrice(seat), 0);

  const allocation = allocateSeatsByCategory({ regularSeats, passengerSelection });
  const passengerBreakdown = {};
  Object.entries(allocation).forEach(([type, seats]) => {
    const subtotal = seats.reduce((sum, seat) => sum + seatPrice(seat), 0);
    passengerBreakdown[type] = {
      ...buildBreakdownEntry(),
      count: seats.length,
      total: subtotal,
      totalSpecialDiscount: subtotal,
      countWithSpecialDiscount: seats.length,
    };
  });

  return {
    amount: total,
    total,
    totalPercentDiscount: '0%',
    totalBeforeDiscount: total,
    discount: 0,
    discountType: pricing.discountType || null,
    passengersCount: regularSeats.length,
    luggage: pricing.luggage || 0,
    taxes: pricing.taxes || 0,
    networks: pricing.networks || 0,
    insurance: pricing.insurance || 0,
    passengerBreakdown,
    hasSingleTripDiscount: false,
    singleTripDiscount: null,
  };
};

/**
 * Gets the passenger total for a selected-seat position.
 * @param {object} options - Helper options.
 * @param {number} options.index - Selected-seat position.
 * @param {number} options.selectedCount - Total selected seats for the trip.
 * @param {object} options.defaultPricing - Trip pricing used as fallback.
 * @param {boolean} options.mixDiscountFaresByAvailability - Whether to mix discount and regular fares when availability is exceeded.
 * @returns {number} Passenger total for the selected-seat position.
 */
export const getPassengerTotalBySelectionIndex = ({
  index,
  selectedCount,
  defaultPricing = {},
  mixDiscountFaresByAvailability = false,
}) => {
  const defaultTotal = Number(defaultPricing?.total || 0);
  const normalTotal = Number(defaultPricing?.totalBeforeDiscount ?? defaultPricing?.total ?? 0);
  const fareAllocation = resolveFareAllocation({
    pricing: defaultPricing,
    selectedCount,
    mixDiscountFaresByAvailability,
  });
  const {
    discountAvailability,
    discountAvailabilityExceeded,
    shouldMixDiscountFares,
    perSeatTotals,
    fallbackDiscount,
  } = fareAllocation;
  const hasValidDiscount = Boolean(
    defaultPricing.providerDiscount && discountAvailability !== null,
  );
  const normalizedSelectedCount = Number(selectedCount);

  if (
    discountAvailabilityExceeded &&
    Number.isFinite(normalizedSelectedCount) &&
    normalizedSelectedCount > discountAvailability
  ) {
    if (shouldMixDiscountFares) return Number(perSeatTotals[index] ?? normalTotal);

    return Number(fallbackDiscount?.total ?? perSeatTotals[index] ?? normalTotal);
  }

  if (!hasValidDiscount || index < discountAvailability) return defaultTotal;

  return normalTotal;
};

/**
 * Calculate selected seats total for pre-checkout pricing.
 * Seat layout prices are only authoritative when seat-based pricing is enabled.
 * Otherwise the trip pricing response is the source of truth.
 * @param {object} options - The options for calculating selected seats total.
 * @param {Array} options.seats - Selected seats.
 * @param {object} options.defaultPricing - Trip pricing used as the per-passenger fallback.
 * @param {boolean} options.useSeatBasedPricing - Whether seat prices should drive totals.
 * @param {boolean} options.mixDiscountFaresByAvailability - Whether to mix discount and regular fares when availability is exceeded.
 * @returns {number} Selected seats total.
 */
export const getSelectedSeatsTotal = ({
  seats = [],
  defaultPricing = {},
  useSeatBasedPricing = false,
  mixDiscountFaresByAvailability = false,
}) => {
  const defaultTotal = Number(defaultPricing?.total || 0);

  if (!seats.length) return 0;
  if (!useSeatBasedPricing) {
    return seats.reduce(
      (sum, _seat, index) =>
        sum +
        getPassengerTotalBySelectionIndex({
          index,
          selectedCount: seats.length,
          defaultPricing,
          mixDiscountFaresByAvailability,
        }),
      0,
    );
  }

  return seats.reduce((sum, seat) => sum + Number(seat.price ?? defaultTotal), 0);
};

/**
 * Custom hook for calculating pricing before checkout.
 * @param {Object} options - The options for the hook.
 * @param {boolean} options.isRoundTrip - The flag to indicate if the trip is a round trip.
 * @returns {Object} Pricing and selection state for pre-checkout.
 * @returns {Object} return.selectedSeats - Selected seats per direction (departs, returns).
 * @returns {number} return.passengersCount - Number of passengers (from depart selected seats).
 * @returns {boolean} return.displayPricingBeforeCheckout - Whether to show pricing before checkout step.
 * @returns {Object} return.departTripPricing - Calculated pricing for departure trip.
 * @returns {Object} return.returnTripPricing - Calculated pricing for return trip (or zeroed if one-way).
 * @returns {number} return.purchaseTotal - Total price for selected seats when displaying before checkout.
 */
const usePricingBeforeCheckout = ({ isRoundTrip }) => {
  const { features } = useSelector((state) => state.whitelabelConfig);
  const pricingBeforeCheckoutIsOn = features.PRICING_BEFORE_CHECKOUT;
  const departs = useSelector((state) => state.purchase.toJS().departs);
  const returns = useSelector((state) => state.purchase.toJS().returns);
  const location = useLocation();
  const purchaseStep = getPurchaseStepName(location.pathname);
  const isBeforeCheckout = ['dSeats', 'rSeats', 'passengers'].includes(purchaseStep);
  const passengersCount = departs?.selectedSeats?.length || 0;
  const displayPricingBeforeCheckout = isBeforeCheckout && pricingBeforeCheckoutIsOn;
  const useSeatBasedPricing = useShowPricesByGroup();
  const mixDiscountFaresByAvailability = useMixDiscountFaresByAvailability();

  // Memoize all pricing calculations to prevent recalculation on every render
  const result = useMemo(() => {
    // When is a round trip the pricing key is different
    const departPricingPerPassenger = isRoundTrip
      ? departs?.fragments[0]?.departureRoundTripPricing
      : departs?.fragments[0]?.pricing;
    const departTripPricing = getTripPricing({
      pricing: departPricingPerPassenger,
      passengersCount,
      mixDiscountFaresByAvailability,
    });

    // When is a round trip the pricing key is different
    const returnPricingPerPassenger = (returns?.fragments || [])[0]?.roundTripPricing;
    const returnTripPricing = returnPricingPerPassenger
      ? getTripPricing({
          pricing: returnPricingPerPassenger,
          passengersCount,
          mixDiscountFaresByAvailability,
        })
      : { amount: 0, discount: 0, total: 0, luggage: 0, taxes: 0, networks: 0, insurance: 0 };

    const departSelectedSeats = departs?.selectedSeats || [];
    const returnSelectedSeats = returns?.selectedSeats || [];

    let precheckoutSeatsTotal = 0;
    const hasSelectedSeats = departSelectedSeats.length > 0 || returnSelectedSeats.length > 0;

    if (displayPricingBeforeCheckout && hasSelectedSeats) {
      const departTotalPrice = getSelectedSeatsTotal({
        seats: departSelectedSeats,
        defaultPricing: departPricingPerPassenger,
        useSeatBasedPricing,
        mixDiscountFaresByAvailability,
      });
      const returnTotalPrice = getSelectedSeatsTotal({
        seats: returnSelectedSeats,
        defaultPricing: returnPricingPerPassenger,
        useSeatBasedPricing,
        mixDiscountFaresByAvailability,
      });

      precheckoutSeatsTotal = departTotalPrice + returnTotalPrice;
    }

    return {
      selectedSeats: {
        departs: departSelectedSeats,
        returns: returnSelectedSeats,
      },
      passengersCount,
      displayPricingBeforeCheckout,
      departTripPricing,
      returnTripPricing,
      purchaseTotal: precheckoutSeatsTotal,
    };
  }, [
    isRoundTrip,
    departs,
    returns,
    passengersCount,
    displayPricingBeforeCheckout,
    useSeatBasedPricing,
    mixDiscountFaresByAvailability,
  ]);

  return result;
};

export default usePricingBeforeCheckout;
